/* Blog hub: topic/group filter, sort, view toggle, load more.
   Carousels are the theme's <content-grid-slider>; they are re-initialised
   after a sort or when they become visible, as Swiper measures on init.
   State is mirrored to ?topic= and ?view= so filtered views can be linked to. */
if (!customElements.get('blog-hub')) {
  class BlogHub extends HTMLElement {
    connectedCallback() {
      this.pageSize = parseInt(this.dataset.pageSize, 10) || 12;
      this.shown = this.pageSize;
      this.filter = { type: 'all', value: '' };
      this.view = this.dataset.defaultView || 'topics';
      this.sortMode = 'newest';

      this.pills = Array.from(this.querySelectorAll('[data-hub-filter]'));
      this.viewButtons = Array.from(this.querySelectorAll('[data-hub-view]'));
      this.viewPanels = Array.from(this.querySelectorAll('[data-hub-view-panel]'));
      this.panels = Array.from(this.querySelectorAll('[data-hub-panel]'));
      this.paged = this.querySelector('[data-hub-paged]');
      this.moreButton = this.querySelector('[data-hub-more]');
      this.empty = this.querySelector('[data-hub-empty]');
      this.status = this.querySelector('[data-hub-status]');
      this.sortSelect = this.querySelector('[data-hub-sort]');

      this.readUrl();

      this.addEventListener('click', this.onClick.bind(this));
      if (this.sortSelect) {
        this.sortSelect.addEventListener('change', () => {
          this.sortMode = this.sortSelect.value;
          this.sort();
          this.shown = this.pageSize;
          this.apply();
        });
      }
      this.apply();
      this.ready = true;
    }

    readUrl() {
      const params = new URLSearchParams(window.location.search);
      const topic = params.get('topic');
      if (topic) {
        const isGroup = topic.indexOf('group:') === 0;
        const value = isGroup ? topic.slice(6) : topic;
        const key = (isGroup ? 'group:' : 'category:') + value;
        if (this.pills.some((pill) => pill.dataset.hubFilter === key)) {
          this.filter = { type: isGroup ? 'group' : 'category', value };
        }
      }
      const view = params.get('view');
      if ((view === 'topics' || view === 'all') && this.viewButtons.length) this.view = view;
    }

    writeUrl() {
      const url = new URL(window.location.href);
      if (this.filter.type === 'all') url.searchParams.delete('topic');
      else url.searchParams.set('topic', (this.filter.type === 'group' ? 'group:' : '') + this.filter.value);
      if (this.view === this.dataset.defaultView) url.searchParams.delete('view');
      else url.searchParams.set('view', this.view);
      window.history.replaceState(window.history.state, '', url.toString());
    }

    onClick(event) {
      const pill = event.target.closest('[data-hub-filter]');
      if (pill) {
        const raw = pill.dataset.hubFilter;
        const split = raw.indexOf(':');
        this.filter = split === -1 ? { type: 'all', value: '' } : { type: raw.slice(0, split), value: raw.slice(split + 1) };
        this.shown = this.pageSize;
        this.apply();
        this.writeUrl();
        return;
      }

      const viewButton = event.target.closest('[data-hub-view]');
      if (viewButton) {
        this.view = viewButton.dataset.hubView;
        this.shown = this.pageSize;
        this.apply();
        this.writeUrl();
        return;
      }

      if (event.target.closest('[data-hub-more]')) {
        this.shown += this.pageSize;
        this.apply();
        return;
      }
    }

    matches(el) {
      if (this.filter.type === 'category') return el.dataset.category === this.filter.value;
      if (this.filter.type === 'group') return el.dataset.group === this.filter.value;
      return true;
    }

    sort() {
      const mode = this.sortMode;
      const compare = (a, b) => {
        if (mode === 'az') return a.dataset.title.localeCompare(b.dataset.title);
        const diff = Number(a.dataset.date) - Number(b.dataset.date);
        return mode === 'oldest' ? diff : -diff;
      };
      this.querySelectorAll('[data-hub-sortable]').forEach((container) => {
        Array.from(container.querySelectorAll(':scope > [data-hub-card]'))
          .sort(compare)
          .forEach((card) => container.appendChild(card));
      });
      this.refreshSliders();
    }

    refreshSliders() {
      if (!this.ready) return;
      this.querySelectorAll('[data-hub-slider]').forEach((slider) => {
        if (slider.offsetParent !== null) slider.dispatchEvent(new CustomEvent('slider:reinitialize'));
      });
    }

    apply() {
      this.pills.forEach((pill) => {
        const raw = pill.dataset.hubFilter;
        const active = this.filter.type === 'all' ? raw === 'all' : raw === this.filter.type + ':' + this.filter.value;
        pill.setAttribute('aria-pressed', String(active));
      });

      this.viewButtons.forEach((button) => {
        button.setAttribute('aria-pressed', String(button.dataset.hubView === this.view));
      });
      this.viewPanels.forEach((panel) => {
        panel.hidden = panel.dataset.hubViewPanel !== this.view;
      });

      let visible = 0;
      if (this.view === 'topics') {
        this.panels.forEach((panel) => {
          panel.hidden = !this.matches(panel);
          if (!panel.hidden) visible += panel.querySelectorAll('[data-hub-card]').length;
        });
      } else if (this.paged) {
        let matched = 0;
        this.paged.querySelectorAll(':scope > [data-hub-card]').forEach((card) => {
          const isMatch = this.matches(card);
          if (isMatch) matched += 1;
          card.hidden = !isMatch || matched > this.shown;
        });
        visible = Math.min(matched, this.shown);
        if (this.moreButton) this.moreButton.hidden = matched <= this.shown;
      }

      if (this.view === 'topics') this.refreshSliders();
      if (this.empty) this.empty.hidden = visible > 0;
      if (this.status) {
        const active = this.pills.find((pill) => pill.getAttribute('aria-pressed') === 'true');
        const label = active ? active.textContent.replace(/\s*\d+\s*$/, '').trim() : '';
        this.status.textContent = 'Showing ' + visible + (visible === 1 ? ' article' : ' articles') + (label ? ' – ' + label : '');
      }
    }

  }

  customElements.define('blog-hub', BlogHub);
}
